import { Box, Button, Flex, H3, H4 } from '@ouster/webviz-react-components';

import { MdCheckCircle, MdRemoveCircle } from 'react-icons/md';
import type {
  MergerAffectedUsersResponse,
  MergerDetailRecord,
} from 'src/components/adminDashboard/types';

import type {
  CommunicationRecipientKey,
  OrgOption,
  StorageStats,
} from '../types';
import { formatBytesToDecimalGb, getOriginalUserRole } from '../utils';

interface CommunicationStepProps {
  survivor: OrgOption;
  sources: OrgOption[];
  storageStats: StorageStats;
  initialAffectedUsersResponse?: MergerAffectedUsersResponse | null;
  selectedRecipientKey: CommunicationRecipientKey | null;
  onRecipientSelect: (recipientKey: CommunicationRecipientKey) => void;
  selectedRecipientByOrgId?: Record<number, string>;
  onEmailRecipientSelect?: (orgId: number, email: string) => void;
  isReadOnly?: boolean;
  sourceUserCountsByOrgId?: Record<number, number>;
  sourceStorageByOrgId?: Record<number, number>;
  targetApproval?: MergerDetailRecord['target_approval'];
  sourceApprovals?: MergerDetailRecord['source_approvals'];
}

type RecipientEntry = {
  key: CommunicationRecipientKey;
  role: 'survivor' | 'source';
  org: OrgOption;
};

const getOrgName = (org: OrgOption) =>
  org.data.org_name || org.data.domain || org.label;

const getSurvivorKey = (orgId: number): CommunicationRecipientKey =>
  `survivor:${orgId}`;

const getSourceKey = (orgId: number): CommunicationRecipientKey =>
  `source:${orgId}`;

const getUsersCount = (org: OrgOption): number => org.data.users?.length ?? 0;

const getUsedStorageGb = (org: OrgOption): number =>
  org.data.usedStorageGb ?? 0;

type AdminRecipientOption = {
  orgId: number;
  orgName: string;
  userId: number;
  email: string;
};

const isAdminRole = (role?: string): boolean =>
  typeof role === 'string' &&
  ['ADMIN', 'SUPER_ADMIN'].includes(role.trim().toUpperCase());

const buildAdminRecipientOptions = (
  initialAffectedUsersResponse: MergerAffectedUsersResponse | null | undefined
): AdminRecipientOption[] => {
  if (!initialAffectedUsersResponse) {
    return [];
  }

  const orgGroups = [
    initialAffectedUsersResponse.target,
    ...initialAffectedUsersResponse.sources,
  ];

  return orgGroups
    .flatMap((group) => {
      const orgId = Number(group.org_id);
      const adminUsers = group.users.filter((user) =>
        isAdminRole(getOriginalUserRole(user))
      );

      return adminUsers.flatMap((user) => {
        const normalizedEmail =
          typeof user.email === 'string' ? user.email.trim() : '';

        if (!normalizedEmail) {
          return [];
        }

        return [
          {
            orgId,
            orgName: group.org_name,
            userId: user.id,
            email: normalizedEmail,
          },
        ];
      });
    })
    .sort((first, second) => {
      if (first.userId !== second.userId) {
        return first.userId - second.userId;
      }

      if (first.orgId !== second.orgId) {
        return first.orgId - second.orgId;
      }

      return first.email.localeCompare(second.email);
    });
};

interface RecipientListProps {
  recipientEntries: RecipientEntry[];
  selectedRecipientKey?: CommunicationRecipientKey;
  onRecipientSelect: (recipientKey: CommunicationRecipientKey) => void;
  approvalStatusByRecipientKey?: Partial<
    Record<CommunicationRecipientKey, 'approved' | 'declined' | 'pending'>
  >;
}

function RecipientList({
  recipientEntries,
  selectedRecipientKey,
  onRecipientSelect,
  approvalStatusByRecipientKey,
}: RecipientListProps) {
  return (
    <Box display="grid" rowGap={3}>
      {recipientEntries.map((entry) => {
        const isActive = entry.key === selectedRecipientKey;
        const approvalStatus = approvalStatusByRecipientKey?.[entry.key];
        return (
          <Box
            key={entry.key}
            as="button"
            type="button"
            textAlign="left"
            p={3}
            borderRadius={8}
            border="1px solid"
            borderColor={isActive ? 'blue.400' : 'blue.500'}
            bg={isActive ? 'blue.700' : 'blackAlpha.100'}
            cursor="pointer"
            transition="all 120ms ease"
            onClick={() => {
              onRecipientSelect(entry.key);
            }}
            data-testid={`merge-recipient-${entry.key}`}
            aria-pressed={isActive}
            _hover={{ bg: isActive ? 'blue.700' : 'blackAlpha.300' }}
            _focusVisible={{
              outline: '2px solid',
              outlineColor: 'blackAlpha.300',
            }}
          >
            <Flex alignItems="center" columnGap={2} mb={1}>
              <Box
                w={2}
                h={2}
                borderRadius="50%"
                bg={isActive ? 'blue.400' : 'blue.500'}
                flexShrink={0}
              />
              <Box color="gray.400" fontSize={12} fontWeight={700}>
                {entry.role.toUpperCase()}
              </Box>
              {approvalStatus === 'approved' ? (
                <Box
                  as={MdCheckCircle}
                  color="green.300"
                  fontSize={16}
                  ml="auto"
                  data-testid={`merge-recipient-status-${entry.key}`}
                  title="Approved"
                  aria-label="Approved"
                />
              ) : null}
              {approvalStatus === 'declined' ? (
                <Box
                  as={MdRemoveCircle}
                  color="red.300"
                  fontSize={16}
                  ml="auto"
                  data-testid={`merge-recipient-status-${entry.key}`}
                  title="Declined"
                  aria-label="Declined"
                />
              ) : null}
            </Flex>
            <Box
              color="white"
              fontWeight={700}
              pl={4}
              whiteSpace="normal"
              overflowWrap="anywhere"
              wordBreak="break-word"
            >
              {getOrgName(entry.org)}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

function MergeActionButtons() {
  return (
    <>
      <Button w="full" mb={2} borderRadius={4} _hover={{}}>
        Approve Merge
      </Button>
      <Button variant="outline" w="full" borderRadius={4} _hover={{}}>
        Decline Request
      </Button>
    </>
  );
}

interface SourceTemplatePreviewProps {
  selectedSource: OrgOption | null;
  survivorName: string;
  sourceAssetsGb: number;
  sourceUsersImpacted: number;
}

function SourceTemplatePreview({
  selectedSource,
  survivorName,
  sourceAssetsGb,
  sourceUsersImpacted,
}: SourceTemplatePreviewProps) {
  const selectedSourceName = selectedSource
    ? getOrgName(selectedSource)
    : 'N/A';

  return (
    <Box p={8} data-testid="merge-source-template">
      <H3 fontSize={24} color="gray.900" mb={4} fontWeight={700}>
        Organization Merger Request
      </H3>

      <Box color="gray.900" mb={4} lineHeight={1.5}>
        A request has been made to merge{' '}
        <Box as="span" fontWeight={600}>
          {selectedSourceName}
        </Box>{' '}
        into the{' '}
        <Box as="span" fontWeight={600}>
          {survivorName}
        </Box>
        .
      </Box>

      <Box color="gray.900" mb={6} lineHeight={1.5}>
        By approving this request, you are agreeing to{' '}
        <Box as="span" fontWeight={700}>
          permanently combine
        </Box>{' '}
        these two entities into one.
      </Box>

      <Box as="p" fontSize={18} color="gray.900" mb={3} fontWeight={700}>
        What happens during this merger?
      </Box>

      <Box
        as="ul"
        color="gray.900"
        mb={8}
        lineHeight={1.5}
        pl={5}
        listStyleType="disc"
      >
        <Box as="li" mb={3}>
          <Box as="span" fontWeight={700}>
            Unified Account:
          </Box>{' '}
          Your entire organization, including all{' '}
          <Box as="span" fontWeight={600}>
            {`${formatBytesToDecimalGb(sourceAssetsGb)} GB`}
          </Box>{' '}
          of data and all users, will be moved into{' '}
          <Box as="span" fontWeight={700}>
            {survivorName}
          </Box>
          .
        </Box>
        <Box as="li" mb={3}>
          <Box as="span" fontWeight={700}>
            Account Closure:
          </Box>{' '}
          Once the merge is complete, this separate organization ({' '}
          <Box as="span" fontWeight={600}>
            {selectedSourceName}
          </Box>{' '}
          ) will be{' '}
          <Box as="span" fontWeight={700}>
            closed
          </Box>
          .
        </Box>
        <Box as="li">
          <Box as="span" fontWeight={700}>
            New Home:
          </Box>{' '}
          You and your{' '}
          <Box as="span" fontWeight={600}>
            {sourceUsersImpacted}
          </Box>{' '}
          users will access all your assets through the{' '}
          <Box as="span" fontWeight={700}>
            {survivorName}
          </Box>{' '}
          dashboard.
        </Box>
      </Box>

      <Box
        p={4}
        borderRadius={4}
        border="1px solid"
        borderColor="yellow.300"
        bg="yellow.50"
        color="yellow.800"
        mb={8}
        lineHeight={1.5}
        display="grid"
        rowGap={4}
      >
        <Box>
          <Box as="span" fontWeight={700}>
            Important:
          </Box>{' '}
          This is a{' '}
          <Box as="span" fontWeight={700}>
            permanent merger
          </Box>
          . Once you click approve, this organization will be absorbed and will
          no longer exist as a standalone account.
        </Box>
        <Box fontWeight={700}>
          Your assets will be accessible in the new unified account.
        </Box>
      </Box>

      <MergeActionButtons />
    </Box>
  );
}

interface StorageStatRowProps {
  label: string;
  value: string;
  labelColor?: string;
  valueColor?: string;
  showDivider?: boolean;
}

function StorageStatRow({
  label,
  value,
  labelColor,
  valueColor,
  showDivider = true,
}: StorageStatRowProps) {
  return (
    <Flex
      justifyContent="space-between"
      px={4}
      py={3}
      borderBottom={showDivider ? '1px solid' : 'none'}
      borderColor={showDivider ? 'gray.300' : undefined}
    >
      <Box color={labelColor} fontWeight={labelColor ? 700 : undefined}>
        {label}
      </Box>
      <Box fontWeight={700} color={valueColor}>
        {value}
      </Box>
    </Flex>
  );
}

interface SurvivorTemplatePreviewProps {
  sourceOrganizations: string[];
  totalUsersImpacted: number;
  storageStats: StorageStats;
}

function SurvivorTemplatePreview({
  sourceOrganizations,
  totalUsersImpacted,
  storageStats,
}: SurvivorTemplatePreviewProps) {
  const mergeCapacityAllotment = Math.max(
    storageStats.newStorageLimit - storageStats.basePlanLimitGb,
    0
  );

  return (
    <Box p={8} data-testid="merge-survivor-template">
      <H3 fontSize={24} color="gray.900" mb={4} fontWeight={700}>
        Organization Consolidation Request
      </H3>
      <Box color="gray.700" mb={3} lineHeight={1.5}>
        An administrator has initiated a request to merge the following
        organization into yours:
      </Box>
      <Box color="gray.700" mb={4} lineHeight={1.5} pl={4} my={4}>
        <Box mb={3}>
          <Box as="span" fontWeight={700}>
            Source Organizations:
          </Box>{' '}
          <Box as="ul" pl={5} mt={1} listStyleType="disc">
            {sourceOrganizations.map((sourceOrganization) => (
              <Box as="li" key={sourceOrganization}>
                {sourceOrganization}
              </Box>
            ))}
          </Box>
        </Box>
        <Box>
          <Box as="span" fontWeight={700}>
            Impact:
          </Box>{' '}
          <Box as="span" fontWeight={600}>
            {totalUsersImpacted}
          </Box>{' '}
          users and{' '}
          <Box as="span" fontWeight={600}>
            {`${formatBytesToDecimalGb(storageStats.incomingGb)} GB`}
          </Box>{' '}
          of data will be added to your account.
        </Box>
      </Box>

      <Box color="gray.700" mb={3} lineHeight={1.5}>
        <Box as="span" fontWeight={700}>
          Storage Impact Summary:
        </Box>
      </Box>

      <Box
        border="1px solid"
        borderColor="gray.300"
        borderRadius={4}
        overflow="hidden"
        mb={8}
      >
        <StorageStatRow
          label="Original Usage"
          value={`${formatBytesToDecimalGb(storageStats.survivorUsedGb)} GB`}
        />
        <StorageStatRow
          label="Incoming Data Volume"
          value={`+${formatBytesToDecimalGb(storageStats.incomingGb)} GB`}
          valueColor="orange.300"
        />
        <StorageStatRow
          label="Merge Capacity Allotment"
          value={`+${formatBytesToDecimalGb(mergeCapacityAllotment)} GB`}
          labelColor="blue.600"
          valueColor="blue.600"
        />
        <StorageStatRow
          label="Final Storage Usage"
          value={`${formatBytesToDecimalGb(
            storageStats.totalRequiredGb
          )} GB (${Math.round(
            storageStats.usedPercent
          )}% of ${formatBytesToDecimalGb(
            Number(storageStats.newStorageLimit ?? 0)
          )} GB)`}
          valueColor="blue.600"
          showDivider={false}
        />
      </Box>

      {storageStats.isReadOnlyRisk ? (
        <Box
          p={4}
          borderRadius={8}
          bg="yellow.50"
          border="1px solid"
          borderColor="yellow.200"
          color="yellow.800"
          mb={8}
          fontSize={13}
          lineHeight={1.5}
        >
          <b>Note on Capacity: </b>
          Capacity risk detected. We recommend increasing the storage override
          before finalizing this merge.
        </Box>
      ) : (
        <Box
          p={4}
          borderRadius={4}
          bg="blue.50"
          border="1px solid"
          borderColor="blue.200"
          color="blue.600"
          mb={8}
          fontSize={13}
          lineHeight={1.5}
        >
          <b>Note on Capacity: </b>
          To accommodate this merge, your organization&apos;s storage cap is
          being increased to{' '}
          {formatBytesToDecimalGb(Number(storageStats.newStorageLimit ?? 0))} GB
          as part of the consolidation allotment.
        </Box>
      )}

      <MergeActionButtons />
    </Box>
  );
}

export default function CommunicationStep({
  survivor,
  sources,
  storageStats,
  initialAffectedUsersResponse = null,
  selectedRecipientKey,
  onRecipientSelect,
  selectedRecipientByOrgId = {},
  onEmailRecipientSelect,
  isReadOnly = false,
  sourceUserCountsByOrgId,
  sourceStorageByOrgId,
  targetApproval = null,
  sourceApprovals = null,
}: CommunicationStepProps) {
  const adminRecipientOptions = buildAdminRecipientOptions(
    initialAffectedUsersResponse
  );
  const recipientEntries: RecipientEntry[] = [
    ...(survivor
      ? [
          {
            key: getSurvivorKey(survivor.data.org_id),
            role: 'survivor' as const,
            org: survivor,
          },
        ]
      : []),
    ...sources.map((source) => ({
      key: getSourceKey(source.data.org_id),
      role: 'source' as const,
      org: source,
    })),
  ];

  const approvalStatusByRecipientKey = recipientEntries.reduce<
    Partial<
      Record<CommunicationRecipientKey, 'approved' | 'declined' | 'pending'>
    >
  >((acc, entry) => {
    if (entry.role === 'survivor') {
      acc[entry.key] =
        targetApproval === true
          ? 'approved'
          : targetApproval === false
          ? 'declined'
          : 'pending';
      return acc;
    }

    const sourceApproval = (sourceApprovals ?? []).find(
      (approval) => Number(approval.org_id) === Number(entry.org.data.org_id)
    );

    acc[entry.key] = sourceApproval
      ? sourceApproval.approved
        ? 'approved'
        : 'declined'
      : 'pending';
    return acc;
  }, {});

  const selectedRecipient =
    recipientEntries.find((entry) => entry.key === selectedRecipientKey) ??
    recipientEntries[0];
  const selectedOrgId = selectedRecipient?.org?.data?.org_id;
  const adminRecipientOptionsForSelectedOrg =
    typeof selectedOrgId === 'number'
      ? adminRecipientOptions.filter((option) => option.orgId === selectedOrgId)
      : [];
  const savedRecipientForSelectedOrg =
    typeof selectedOrgId === 'number'
      ? selectedRecipientByOrgId[selectedOrgId]
      : undefined;
  const savedRecipientExistsInOptions =
    typeof savedRecipientForSelectedOrg === 'string' &&
    adminRecipientOptionsForSelectedOrg.some(
      (option) => option.email === savedRecipientForSelectedOrg
    );
  const recipientOptionsForSelectedOrg = adminRecipientOptionsForSelectedOrg;
  const selectedEmailRecipientValue =
    typeof selectedOrgId === 'number'
      ? savedRecipientExistsInOptions
        ? savedRecipientForSelectedOrg ?? ''
        : recipientOptionsForSelectedOrg[0]?.email ?? ''
      : '';

  const isSourceTemplate = selectedRecipient?.role === 'source';
  const selectedSource = isSourceTemplate ? selectedRecipient.org : null;
  const sourceUsersImpacted = selectedSource
    ? sourceUserCountsByOrgId?.[selectedSource.data.org_id] ??
      getUsersCount(selectedSource)
    : 0;
  const sourceAssetsGb = selectedSource
    ? sourceStorageByOrgId?.[selectedSource.data.org_id] ??
      getUsedStorageGb(selectedSource)
    : 0;
  const totalUsersImpacted = sources.reduce((sum, source) => {
    const sourceUserCount =
      sourceUserCountsByOrgId?.[source.data.org_id] ?? getUsersCount(source);
    return sum + sourceUserCount;
  }, 0);
  const sourceOrganizations = sources
    .map((source) => {
      const sourceEmail = source.data.creator_email?.trim();
      const sourceName = getOrgName(source);
      return `${sourceName} (${sourceEmail})`;
    })
    .filter((sourceOrganization) => sourceOrganization.length > 0);
  const sourceOrganizationList =
    sourceOrganizations.length > 0 ? sourceOrganizations : ['N/A'];
  const survivorName = survivor ? getOrgName(survivor) : 'N/A';

  return (
    <Box
      data-testid="merge-step-communication"
      display="grid"
      rowGap={4}
      overflow="auto"
      h="50vh"
      pr={6}
    >
      <Box>
        <H3 fontSize={26} mb={2}>
          Communication Preview
        </H3>
        <Box color="gray.300" fontSize={14} mb={4}>
          Review the automated notifications that will be sent to organization
          admin.
        </Box>
      </Box>

      <Flex border="1px solid" borderColor="whiteAlpha.200" borderRadius={8}>
        {/* EMAIL RECIPIENT */}
        <Box
          w="280px"
          borderRight="1px solid"
          borderColor="whiteAlpha.200"
          p={4}
        >
          <H4 fontSize={14} mb={4} color="gray.300">
            EMAIL RECIPIENT
          </H4>
          <RecipientList
            recipientEntries={recipientEntries}
            selectedRecipientKey={selectedRecipient?.key}
            onRecipientSelect={onRecipientSelect}
            approvalStatusByRecipientKey={approvalStatusByRecipientKey}
          />
        </Box>

        {/* COMMUNICATION PREVIEW */}
        <Box flex={1} p={4} overflow="auto">
          <Box
            mx="auto"
            bg="gray.50"
            color="gray.900"
            overflow="hidden"
            borderRadius={8}
            data-testid="merge-communication-preview-card"
          >
            <Box
              px={6}
              py={3}
              borderBottom="1px solid"
              borderColor="gray.200"
              bg="gray.200"
            >
              <Flex alignItems="center" columnGap={3} mb={2}>
                <Box
                  color="gray.500"
                  fontSize={14}
                  fontWeight={600}
                  minW="28px"
                >
                  To:
                </Box>
                {recipientOptionsForSelectedOrg.length > 0 ? (
                  <Box
                    as="select"
                    value={selectedEmailRecipientValue}
                    onChange={(event: { target: { value: string } }) => {
                      if (typeof selectedOrgId !== 'number') {
                        return;
                      }

                      const nextEmail = event.target.value;
                      onEmailRecipientSelect?.(selectedOrgId, nextEmail);
                    }}
                    aria-readonly={isReadOnly}
                    disabled={isReadOnly || typeof selectedOrgId !== 'number'}
                    border="1px solid"
                    borderColor="gray.300"
                    borderRadius={4}
                    px={3}
                    py={2}
                    bg="white"
                    color="gray.700"
                    fontSize={14}
                    lineHeight={1.2}
                    width="fit-content"
                    maxW="100%"
                    data-testid="merge-communication-header-recipient"
                  >
                    {recipientOptionsForSelectedOrg.map((option) => (
                      <option
                        key={`${option.orgId}:${option.userId}:${option.email}`}
                        value={option.email}
                      >
                        {option.email}
                      </option>
                    ))}
                  </Box>
                ) : (
                  <Box color="gray.500" fontSize={14}>
                    No admins found.
                  </Box>
                )}
              </Flex>
              <Flex alignItems="center" columnGap={3}>
                <Box
                  color="gray.500"
                  fontSize={14}
                  fontWeight={600}
                  minW="56px"
                >
                  Subject:
                </Box>
                <Box color="gray.700" fontSize={14} fontWeight={600}>
                  Action Required: Organization Merger Request
                </Box>
              </Flex>
            </Box>
            {isSourceTemplate ? (
              <SourceTemplatePreview
                selectedSource={selectedSource}
                survivorName={survivorName}
                sourceAssetsGb={sourceAssetsGb}
                sourceUsersImpacted={sourceUsersImpacted}
              />
            ) : (
              <SurvivorTemplatePreview
                sourceOrganizations={sourceOrganizationList}
                totalUsersImpacted={totalUsersImpacted}
                storageStats={storageStats}
              />
            )}
          </Box>
        </Box>
      </Flex>
    </Box>
  );
}
